import { useQuery, useQueryClient } from '@tanstack/react-query';
import { includes, map, some } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { DeviceSensorConfiguration, DeviceSensorConfigurations, DeviceSensorInfos } from '@/api/device/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { QueryConfig, pickQueryStatus, queryClient } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useOptionalSpotterContext } from '@/context/SpotterContext';
import { useViewToken } from '@/context/ViewTokenContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { composeSensorInfo } from '@/utils';
import { addMockSensorConfig, addMockSensorConfigs } from '@/utils/mockDataHelpers';

type GetDeviceSensorConfigResponse = { sensor_configurations?: DeviceSensorConfiguration[] };

/**
 * This is when we stopped writing sensor configs every time we get a new report. Cutting queries off here dramatically reduces the cardinality of
 * results, and is good enough if we only want to consider configs containing sensor types that were introduced after this date (e.g. hydrophone).
 */
const SENSOR_CONFIG_LOOKBACK_INITIAL_TS = new Date('2025-06-09').getTime();
const CURRENT_SENSOR_CONFIG_STALE_TIME_MS = 1000 * 60 * 5; // 5 minutes

const currentSensorConfigurationQueryKey = (spotterId: string | null | undefined, viewToken?: string | null) => [
    'device-current-sensor-configuration',
    { spotterId },
    { isPublic: Boolean(viewToken) },
];

const getDeviceCurrentSensorConfiguration = async (
    {
        spotterId,
    }: {
        spotterId: string;
    },
    options?: RequestOptions
): Promise<DeviceSensorConfiguration[]> => {
    const res = await axios.get<GetDeviceSensorConfigResponse, GetDeviceSensorConfigResponse>(
        `/user-rest/devices/${spotterId}/current-sensor-configuration`,
        {
            ...requestConfig(options),
        }
    );
    return res.sensor_configurations ?? [];
};

type UseCurrentSensorConfigurationOptions = {
    config?: QueryConfig<typeof getDeviceCurrentSensorConfiguration>;
} & { spotterId?: string | null };

export const useCurrentSensorConfiguration = ({
    config: { enabled = true, ...config } = {},
    spotterId: spotterIdParam,
}: UseCurrentSensorConfigurationOptions = {}) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: spotterIdParam });
    const { loading: loadingToken, viewToken } = useViewToken();
    return useQuery<DeviceSensorConfiguration[]>({
        queryKey: currentSensorConfigurationQueryKey(spotterId, viewToken),
        queryFn: ({ signal }) => getDeviceCurrentSensorConfiguration({ spotterId: spotterId! }, { viewToken, signal }),
        enabled: !!spotterId && enabled && !loadingToken,
        staleTime: CURRENT_SENSOR_CONFIG_STALE_TIME_MS,
        ...config,
    });
};

export type CurrentSensorConfigurationBySpotterId = Record<string, DeviceSensorConfiguration[]>;

type UseDevicesCurrentSensorConfigurationOptions = {
    spotterIds: string[];
    config?: QueryConfig<typeof getDevicesCurrentSensorConfiguration>;
};

/**
 * Each spotter is fetched as a cache entry of its own, which `useCurrentSensorConfiguration` observes
 * directly and which `fetchQuery` joins rather than duplicates. So this aggregation deliberately hands its
 * own signal to none of them: an entry with no observer of its own is outside react-query's abort
 * accounting, and cancelling one here would fail the request a mounted `useCurrentSensorConfiguration` had
 * joined. Abandoning this query therefore leaves its per-spotter requests to finish, and they land in the
 * cache for the next caller.
 */
const getDevicesCurrentSensorConfiguration = async (
    spotterIds: string[],
    viewToken?: string | null
): Promise<CurrentSensorConfigurationBySpotterId> => {
    const currentSensorConfigurations = await Promise.all(
        spotterIds.map(async (spotterId) => {
            const currentSensorConfiguration = await queryClient.fetchQuery({
                queryKey: currentSensorConfigurationQueryKey(spotterId, viewToken),
                queryFn: () => getDeviceCurrentSensorConfiguration({ spotterId }, { viewToken }),
                staleTime: CURRENT_SENSOR_CONFIG_STALE_TIME_MS,
            });

            return [spotterId, currentSensorConfiguration] as const;
        })
    );

    return Object.fromEntries(currentSensorConfigurations);
};

export const useDevicesCurrentSensorConfiguration = ({
    spotterIds = [],
    config: { enabled = true, ...config } = {},
}: UseDevicesCurrentSensorConfigurationOptions) => {
    const { viewToken } = useViewToken();
    const mockDataFlag = useFeatureFlag('mockData');

    const result = useQuery<CurrentSensorConfigurationBySpotterId>({
        queryKey: ['devices-current-sensor-configuration', spotterIds],
        queryFn: () => getDevicesCurrentSensorConfiguration(spotterIds, viewToken),
        enabled: Boolean(enabled && spotterIds.length > 0),
        staleTime: CURRENT_SENSOR_CONFIG_STALE_TIME_MS,
        ...config,
    });

    if (mockDataFlag && result.data) {
        Object.values(result.data).map(addMockSensorConfig);
    }

    return result;
};

export type GetDeviceSensorConfigurationsResponse = APIResponse<
    {
        id: number;
        timestamp: string;
        configuration: { sensor_configurations: DeviceSensorConfiguration[]; configuration_checksum: number };
    }[]
>;

const getDeviceSensorConfigurations = async (
    { spotterId, beforeTs, aftertTs }: GetDeviceSensorConfigurationsParams,
    options?: RequestOptions
): Promise<DeviceSensorConfigurations> => {
    const res = await axios.get<GetDeviceSensorConfigurationsResponse, GetDeviceSensorConfigurationsResponse>(
        `/user-rest/devices/${spotterId}/sensor-configurations`,
        {
            params: {
                ...(beforeTs ? { before_epoch_ms: beforeTs } : null),
                ...(aftertTs ? { since_epoch_ms: aftertTs } : null),
            },
            ...requestConfig(options),
        }
    );

    addMockSensorConfigs(res.data);

    return res.data.map((config) => {
        const { timestamp, configuration } = config;
        const { sensor_configurations, configuration_checksum } = configuration;
        return {
            sensor_configurations,
            timestamp,
            configuration_checksum,
        };
    });
};

type GetDeviceSensorConfigurationsParams = { spotterId?: string | null; beforeTs?: number; aftertTs?: number };

type UseSensorConfigurationsOptions = {
    config?: QueryConfig<typeof getDeviceSensorConfigurations>;
} & GetDeviceSensorConfigurationsParams;

const SENSOR_CONFIGS_QUERY_KEY = 'device-sensor-configurations';

export const useSensorConfigurations = ({
    config: { enabled = true, ...config } = {},
    ...params
}: UseSensorConfigurationsOptions) => {
    const { loading: loadingToken, viewToken } = useViewToken();

    return useQuery<DeviceSensorConfigurations>({
        queryKey: [SENSOR_CONFIGS_QUERY_KEY, params, { isPublic: Boolean(viewToken) }],
        queryFn: ({ signal }) => getDeviceSensorConfigurations(params, { viewToken, signal }),
        enabled: !!params.spotterId && enabled && !loadingToken,
        staleTime: 1000 * 60 * 5, // 5 minutes
        ...config,
    });
};

export type UseSensorInfoByIdParams = { spotterId?: string | null; sensorConfigChecksums: string[] };

const formatChecksum = (configChecksum: number) =>
    configChecksum ? `0x${configChecksum.toString(16).padStart(8, '0')}` : '';

/**
 * Returns all the requested IDs, and attempts to refresh configs if any unknown IDs are requested (although won't try again if they're still don't show up
 * on the subsequent request).
 *
 * NB: Only returns configs newer than {@link SENSOR_CONFIG_LOOKBACK_INITIAL_TS}. If we want to go older, we probably ought to start de-duping server-side.
 */
export const useSensorInfoById = ({ spotterId, sensorConfigChecksums }: UseSensorInfoByIdParams) => {
    const queryClient = useQueryClient();
    const [allConfigChecksums, setAllConfigChecksums] = useState(new Set(sensorConfigChecksums));

    const response = useSensorConfigurations({ spotterId, aftertTs: SENSOR_CONFIG_LOOKBACK_INITIAL_TS });
    const sensorConfigs = response.data;

    const data: DeviceSensorInfos | undefined = useMemo(() => {
        if (!sensorConfigs) return undefined;

        const requestedChecksums = new Set(sensorConfigChecksums);
        const infos: DeviceSensorInfos = {};

        for (const { sensor_configurations, timestamp, configuration_checksum } of sensorConfigs) {
            const checksum = formatChecksum(configuration_checksum);
            if (requestedChecksums.has(checksum)) {
                infos[checksum] = { timestamp, sensorConfigurations: composeSensorInfo(sensor_configurations) };
            }
        }

        return infos;
    }, [sensorConfigChecksums, sensorConfigs]);

    useEffect(() => {
        if (!sensorConfigs || !data) return;

        const requestedChecksums = sensorConfigChecksums;
        const responseChecksums = new Set(
            map(sensorConfigs, ({ configuration_checksum }) => formatChecksum(configuration_checksum))
        );
        const newAllIds = new Set([...requestedChecksums, ...responseChecksums]);

        if (requestedChecksums.filter((c) => !allConfigChecksums.has(c) && !responseChecksums.has(c)).length) {
            // IDs have been requested which have never been requested before and are not already present in the result set
            queryClient.invalidateQueries({
                queryKey: [SENSOR_CONFIGS_QUERY_KEY],
            });
        }

        if (
            newAllIds.size !== allConfigChecksums.size ||
            [...newAllIds].filter((c) => !allConfigChecksums.has(c)).length
        ) {
            // allConfigIds and newAllConfigs have different elements (this could be `if (newAllConfigs.symmetricDifference(allConfigs).size)` on newer ESx)
            setAllConfigChecksums(newAllIds);
        }
    }, [data, sensorConfigChecksums, sensorConfigs, allConfigChecksums, queryClient]);

    return { ...pickQueryStatus(response), data };
};

export type SensorType =
    | 'currents'
    | 'waterTemperature'
    | 'pressure'
    | 'dissolvedOxygen'
    | 'salinity'
    | 'hydrophone'
    | 'turbidity';

type UseDeviceHasSensorOptions = {
    spotterId?: string | null;
    // abstract away the signals we use to determine particular sensors, just to consolidate the logic in case we
    // come up with a better scheme
    type: SensorType;
};

// not clear that this is the best way to tell; don't export this
const APP_NAMES_BY_SENSOR_TYPE: Record<SensorType, string[]> = {
    currents: ['aanderaa'],
    waterTemperature: ['bm_soft_module', 'bm_rbr', 'aanderaa', 'aanderaa_salinity'],
    pressure: ['bm_rbr'],
    dissolvedOxygen: ['pme_do_sensor'],
    salinity: ['aanderaa_salinity'],
    hydrophone: ['borealis', 'borealis2'],
    turbidity: ['seapoint_turbidity'],
};

export const useDeviceHasSensor = ({ spotterId, type }: UseDeviceHasSensorOptions) => {
    const { data: sensorConfig, isPending } = useCurrentSensorConfiguration({ spotterId });

    const hasSensor = useMemo(() => {
        if (!sensorConfig) {
            return false;
        }
        return sensorConfigurationIncludesType(sensorConfig, type);
    }, [sensorConfig, type]);

    return { hasSensor, isPending };
};

export const sensorIncludesType = (sensorConfiguration: DeviceSensorConfiguration, sensorType: SensorType) =>
    !!APP_NAMES_BY_SENSOR_TYPE[sensorType]?.includes(sensorConfiguration.app_name);

export const sensorConfigurationIncludesType = (
    sensorConfiguration: DeviceSensorConfiguration[],
    sensorType: SensorType
) => {
    return some(sensorConfiguration, (config) => includes(APP_NAMES_BY_SENSOR_TYPE[sensorType], config?.app_name));
};
